export const UPLOAD_CATEGORY_KINDS = [
  "image",
  "audio",
  "video",
  "voice",
  "doc",
  "excel",
  "ppt",
  "txt",
  "code",
  "zip",
] as const;

export type UploadCategoryKind = (typeof UPLOAD_CATEGORY_KINDS)[number];
export type UploadKind = UploadCategoryKind | "file";
export const UPLOAD_KINDS: UploadKind[] = [...UPLOAD_CATEGORY_KINDS, "file"];

export const UPLOAD_EXT_SETTING_KEYS: Record<UploadCategoryKind, string> = {
  image: "image_ext",
  audio: "audio_ext",
  video: "video_ext",
  voice: "voice_ext",
  doc: "doc_ext",
  excel: "excel_ext",
  ppt: "ppt_ext",
  txt: "txt_ext",
  code: "code_ext",
  zip: "zip_ext",
};

export const UPLOAD_MAX_SIZE_SETTING_KEYS: Record<UploadCategoryKind, string> = {
  image: "image_max_mb",
  audio: "audio_max_mb",
  video: "video_max_mb",
  voice: "voice_max_mb",
  doc: "doc_max_mb",
  excel: "excel_max_mb",
  ppt: "ppt_max_mb",
  txt: "txt_max_mb",
  code: "code_max_mb",
  zip: "zip_max_mb",
};

export const DEFAULT_UPLOAD_MAX_SIZE_MB = 50;

export const UPLOAD_KIND_LABELS: Record<UploadKind, string> = {
  image: "图片",
  audio: "音频",
  video: "视频",
  voice: "语音",
  doc: "文档",
  excel: "表格",
  ppt: "幻灯片",
  txt: "文本",
  code: "代码",
  zip: "压缩包",
  file: "全部",
};

export const DEFAULT_UPLOAD_EXTENSIONS: Record<UploadCategoryKind, string[]> = {
  image: ["jpg", "png", "gif", "webp", "svg", "bmp", "ico"],
  audio: ["mp3", "wav", "m4a", "aac", "ogg", "flac", "wma"],
  video: ["mp4", "avi", "mov", "mkv"],
  voice: ["voice"],
  doc: ["doc", "docx", "pdf"],
  excel: ["xls", "xlsx", "csv"],
  ppt: ["ppt", "pptx"],
  txt: ["txt", "md"],
  code: ["py", "java", "js", "css"],
  zip: ["zip", "rar", "7z"],
};

const EXTENSION_KIND_MAP: Record<string, UploadCategoryKind> = {
  jpg: "image",
  jpeg: "image",
  png: "image",
  gif: "image",
  webp: "image",
  bmp: "image",
  svg: "image",
  svgz: "image",
  ico: "image",
  apng: "image",
  avif: "image",
  heic: "image",
  heif: "image",
  jfif: "image",
  tif: "image",
  tiff: "image",
  mp3: "audio",
  wav: "audio",
  aac: "audio",
  flac: "audio",
  ogg: "audio",
  m4a: "audio",
  wma: "audio",
  mp4: "video",
  avi: "video",
  mov: "video",
  mkv: "video",
  wmv: "video",
  flv: "video",
  webm: "video",
  m4v: "video",
  mpeg: "video",
  mpg: "video",
  "3gp": "video",
  voice: "voice",
  doc: "doc",
  docx: "doc",
  pdf: "doc",
  rtf: "doc",
  odt: "doc",
  xls: "excel",
  xlsx: "excel",
  csv: "excel",
  ods: "excel",
  ppt: "ppt",
  pptx: "ppt",
  odp: "ppt",
  txt: "txt",
  md: "txt",
  log: "txt",
  py: "code",
  java: "code",
  js: "code",
  ts: "code",
  jsx: "code",
  tsx: "code",
  css: "code",
  html: "code",
  json: "code",
  c: "code",
  cpp: "code",
  go: "code",
  rs: "code",
  php: "code",
  rb: "code",
  sh: "code",
  sql: "code",
  zip: "zip",
  rar: "zip",
  "7z": "zip",
  gz: "zip",
  bz2: "zip",
  tar: "zip",
  xz: "zip",
};

export function deriveUploadKind(extension: string): UploadKind {
  const normalized = extension.trim().toLowerCase();

  return EXTENSION_KIND_MAP[normalized === "jpeg" ? "jpg" : normalized] ?? "file";
}

export function isUploadKind(value: string): value is UploadKind {
  return (UPLOAD_KINDS as string[]).includes(value);
}

export function getFileExtension(fileName: string) {
  const ext = fileName.includes(".")
    ? fileName.split(".").pop()!.toLowerCase()
    : "";

  return ext === "jpeg" ? "jpg" : ext;
}

export function normalizeExtensionList(extensions: unknown, fallback: string[]) {
  if (!Array.isArray(extensions)) {
    return [...fallback];
  }

  const normalized = extensions
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim().toLowerCase().replace(/^\./, ""))
    .filter(Boolean);

  return normalized.length > 0 ? normalized : [...fallback];
}

export function dedupeExtensions(extensions: string[]) {
  const seen = new Set<string>();

  return extensions.filter((ext) => {
    const key = ext === "jpeg" ? "jpg" : ext;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

export function parseExtensionsInput(input: string) {
  return dedupeExtensions(
    input
      .split(/[,\s\n]+/)
      .map((item) => item.trim().toLowerCase().replace(/^\./, ""))
      .filter(Boolean),
  );
}

export function formatExtensionsInput(extensions: string[]) {
  return extensions.join(", ");
}

export function buildAcceptAttribute(extensions: string[]) {
  return extensions
    .flatMap((ext) => (ext === "jpg" ? [".jpg", ".jpeg"] : [`.${ext}`]))
    .join(",");
}

export const PROFILE_AVATAR_EXTENSIONS = ["jpg", "png", "webp"] as const;

export const PROFILE_AVATAR_EXTENSION_NAMES = ["jpg", "jpeg", "png", "webp"] as const;

export const PROFILE_AVATAR_EXTENSION_HINT = PROFILE_AVATAR_EXTENSION_NAMES.join("、");

export const PROFILE_AVATAR_ACCEPT = buildAcceptAttribute([
  ...PROFILE_AVATAR_EXTENSIONS,
]);

export function isUploadFileAllowed(
  fileName: string,
  fileSize: number,
  rules: { extensions: string[]; maxSizeMb: number },
) {
  if (fileSize <= 0) {
    return { ok: false as const, reason: "empty" as const, message: "请选择文件" };
  }

  if (fileSize > rules.maxSizeMb * 1024 * 1024) {
    return {
      ok: false as const,
      reason: "tooLarge" as const,
      message: `文件大小不能超过 ${rules.maxSizeMb} MB`,
    };
  }

  const extension = getFileExtension(fileName);

  if (!extension || !rules.extensions.includes(extension)) {
    return {
      ok: false as const,
      reason: "extension" as const,
      message: `仅支持：${rules.extensions.join("、")}`,
    };
  }

  return { ok: true as const };
}
